import { find, get, map, filter } from "lodash";
import * as yup from "yup";
import { useRef, useState } from "react";
import { Formik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { AddOutlined } from "@mui/icons-material";

import { useGroup } from "../hooks";

import FormikTextField from "components/base/FormikTextField";
import FormikSelectField from "components/base/FormikSelectField";
import {
  getDefaultGradeLevelForRole,
  getFormikGradeOptionsForRole,
  isGradeAllowedForRole,
} from "../utils/grade-options";
import { isgradneeded } from "config";

export default function AddGroupUserModal({ open, handleClose }) {
  const { groupRoles, group, addUser } = useGroup();

  const formRef = useRef();

  const schema = yup.object().shape({
    email: yup.string().email().required().label("Email Address"),
    ...(isgradneeded ? {
      gradeLevel: yup
        .string()
        .test(
          "valid-grade-level-for-role",
          "Grade Level is not valid for the selected role",
          function (value) {
            return isGradeAllowedForRole(this.parent.role, value);
          }
        )
        .label("Grade Level")
        .required(),
    } : {
      licenseLevel: yup
        .string()
        .oneOf(
          map(group?.licenseLevels, "key"),
          ({ label }) =>
            `${label} must be one of the following values: ${map(
              group?.licenseLevels,
              "label"
            ).join(", ")}`
        )
        .label("License Level")
        .required(),
    }),
    role: yup
      .string()
      .oneOf(
        map(groupRoles, "key"),
        ({ label }) =>
          `${label} must be one of the following values: ${map(
            groupRoles,
            "label"
          ).join(", ")}`
      )
      .label("Group Role")
      .required(),
  });

  const defaultGroupRole = find(groupRoles, { default: true });
  const defaultGroupRoleKey = defaultGroupRole ? get(defaultGroupRole, "key") : "";
  const [initialState] = useState({
    email: "",
    ...(isgradneeded ? {
      gradeLevel: getDefaultGradeLevelForRole(defaultGroupRoleKey),
    } : {
      licenseLevel: get(group?.licenseLevels?.[0], "key") || "",
    }),
    role: defaultGroupRoleKey,
  });

  async function onFormSubmit(values) {
    const { email, gradeLevel, licenseLevel, role } = values;
    const response = await addUser(email, isgradneeded ? gradeLevel : licenseLevel, role);
    if (response.newUser != null) {
      if (formRef.current) {
        formRef.current.handleReset();
      }
      handleClose();
    }
  }

  return (
    <Formik
      initialValues={{ ...initialState }}
      enableReinitialize={true}
      validationSchema={schema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={onFormSubmit}
      innerRef={formRef}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleReset,
        handleSubmit,
        setFieldValue,
        setValues,
      }) => {
        const { email, gradeLevel, licenseLevel, role } = values;
        function resetAndClose(event) {
          handleReset(event);
          handleClose();
        }
        return (
          <Dialog
            open={open}
            onClose={resetAndClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              component: "form",
              onSubmit: handleSubmit,
            }}
          >
            <DialogTitle>Add User</DialogTitle>
            <DialogContent
              sx={{
                overflowY: "revert",
              }}
            >
              <Stack direction="column">
                <DialogContentText>
                  Invite new or existing user to join your Group.
                </DialogContentText>
                <FormikTextField
                  fullWidth
                  error={get(errors, "email")}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  value={email}
                  label="Email Address"
                  name="email"
                />
                <Stack direction="row">
                  {isgradneeded ? (
                    <FormikSelectField
                      error={get(errors, "gradeLevel")}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      value={gradeLevel}
                      label="What is your grade level?"
                      name="gradeLevel"
                      id="license-level-select"
                      options={getFormikGradeOptionsForRole(role)}
                      sx={{
                        flex: 1,
                      }}
                    />
                  ) : (
                    group?.licenseLevels?.length > 0 && (
                      <FormikSelectField
                        error={get(errors, "licenseLevel")}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        value={licenseLevel}
                        label="License Level"
                        name="licenseLevel"
                        id="license-level-select"
                        options={filter(
                          group.licenseLevels,
                          (level) => level.key !== "Unknown"
                        )}
                        disabled={group.licenseLevels?.length <= 1}
                        sx={{
                          flex: 1,
                        }}
                      />
                    )
                  )}
                  {groupRoles && groupRoles.length && (
                    <FormikSelectField
                      error={get(errors, "role")}
                      handleBlur={handleBlur}
                      handleChange={(event) => {
                        const nextRole = event.target.value;
                        const nextGradeLevel =
                          nextRole === "OrganizationAdmin" ||
                          nextRole === "Teacher"
                            ? "Educator"
                            : isGradeAllowedForRole(nextRole, gradeLevel)
                            ? gradeLevel
                            : getDefaultGradeLevelForRole(nextRole);

                        setValues({
                          ...values,
                          role: nextRole,
                          ...(isgradneeded && { gradeLevel: nextGradeLevel }),
                        });
                      }}
                      value={role}
                      label="User Group Role"
                      name="role"
                      id="group-role-select"
                      options={groupRoles}
                      sx={{
                        flex: 1,
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions
              sx={{
                backgroundColor: "secondary.main",
              }}
            >
              <Button variant="outlined" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button startIcon={<AddOutlined />} type="submit">
                Add
              </Button>
            </DialogActions>
          </Dialog>
        );
      }}
    </Formik>
  );
}
